import { Box } from "@mui/material";
import { useMemo, useState } from "react";

import { VehicleDamageDataViewModal } from "@/common/components/Entity/VehicleDamage/View/VehicleDamageDataViewModal";
import VehicleDamageViewModal from "@/common/components/Entity/VehicleDamage/View/VehicleDamageViewModal";
import { noop } from "@/common/helpers/function";
import { TypeHelper } from "@/common/helpers/type";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { Mapper } from "@/common/mapping/mapper";
import { cast } from "@/common/ts/conversions";
import { GenericDamage } from "@/common/ts/visualModel";
import { apiClient } from "@/core/api/ApiClient";
import {
  DamageCorrectionStatus,
  DamageCostEvaluationDto,
  VehicleDamageDataDto,
  VehicleDamageDto,
} from "@/core/api/generated/v1";

import {
  GenericDamageViewerMode,
  VisualModelEditorOfGenericDamage,
  VisualModelEditorOfGenericDamageProps,
} from "../GenericDamage/VisualModelEditorOfGenericDamage";

// interface GenericDamageLocal extends GenericDamage<VehicleDamageDataDto | VehicleDamageDto> {
//   data: VehicleDamageDataDto | VehicleDamageDto;
// }

interface GenericDamageCurrent extends GenericDamage<VehicleDamageDataDto> {
  type: "current";
  data: VehicleDamageDataDto;
}

interface GenericDamageOriginal extends GenericDamage<VehicleDamageDto> {
  type: "original";
  data: VehicleDamageDto;
}

type GenericDamageLocal = GenericDamageCurrent | GenericDamageOriginal;

interface Props {
  entity: DamageCostEvaluationDto;
  /** current - all damages in items.
  /** currentCorrected - corrected damages in items.
   *  initial - initial damages specified during DCE creation from DD, C2C request, etc. (if any).
   *  correctionsOriginal - original for inline corrected damages.
   *  initialAndCorrectionsOriginal - initial + original for inline corrected damages.
   */
  damagesToDisplay?:
    | "current"
    | "currentCorrected"
    | "initial"
    | "correctionsOriginal"
    | "initialAndCorrectionsOriginal";
  editorProps?: Partial<VisualModelEditorOfGenericDamageProps<GenericDamageLocal>>;
}

export function VisualModelViewerOfDamageCostEvaluation({
  entity,
  damagesToDisplay = "current",
  editorProps,
}: Props) {
  const [damageDataToView, setDamageDataToView] = useState<Nil<VehicleDamageDataDto>>(null);
  const [damageToView, setDamageToView] = useState<Nil<VehicleDamageDto>>(null);

  // fetch initial vehicle damages
  const vehicleDamageIdsToFetch = useMemo(
    () =>
      damagesToDisplay === "initial"
        ? entity.initialVehicleDamageIds
        : damagesToDisplay === "correctionsOriginal"
          ? entity.corrections?.originalVehicleDamageIds
          : damagesToDisplay === "initialAndCorrectionsOriginal"
            ? entity.corrections?.initialAndOriginalVehicleDamageIds
            : undefined,
    [damagesToDisplay, entity],
  );
  const originalVehicleDamagesRequest = useApiRequest(
    apiClient.vehicleDamagesApi.apiV1VehiclesVehicleIdDamagesByIdsGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      vehicleId: entity.vehicle?.id || "",
      vehicleDamageIds: vehicleDamageIdsToFetch || [],
    },
    {
      deps: [entity.vehicle?.id, vehicleDamageIdsToFetch],
      skip: !entity.vehicle?.id || TypeHelper.isEmptyArray(vehicleDamageIdsToFetch),
    },
  );
  const originalVehicleDamages = originalVehicleDamagesRequest.data;

  const currentDamagesData = useMemo(
    () =>
      entity.items?.flatMap((x) => x.damages?.map((y) => y.damage!).filter(Boolean) || []) || [],
    [entity],
  );
  const currentCorrectedDamagesData = useMemo(
    () =>
      currentDamagesData.filter(
        (x) => x.dataCorrectionStatusComputed === DamageCorrectionStatus.Corrected,
      ),
    [currentDamagesData],
  );
  const damagesData =
    damagesToDisplay === "current"
      ? currentDamagesData
      : damagesToDisplay === "currentCorrected"
        ? currentCorrectedDamagesData
        : [];

  const genericDamages = useMemo<GenericDamageLocal[]>(
    () =>
      damagesToDisplay === "current" || damagesToDisplay === "currentCorrected"
        ? damagesData
            .map((x) => Mapper.visualModel.mapVehicleDamageDataDtoToGenericDamage(x, { data: x }))
            .map((genericDamage) =>
              cast<GenericDamageLocal>({
                ...genericDamage,
                type: "current",
                data: genericDamage.data!,
              }),
            )
        : originalVehicleDamages
            ?.map((x) => Mapper.visualModel.mapVehicleDamageDtoToGenericDamage(x, { data: x }))
            .map((genericDamage) =>
              cast<GenericDamageLocal>({
                ...genericDamage,
                type: "original",
                data: genericDamage.data!,
              }),
            ) || [],
    [damagesData, originalVehicleDamages],
  );

  return (
    <Box>
      {entity.vehicle?.id && (
        <VisualModelEditorOfGenericDamage
          {...editorProps}
          mode={GenericDamageViewerMode.View}
          vehicleId={entity.vehicle.id}
          visualModelId={undefined}
          visualModel={undefined}
          isAutoPickVisualModel
          damages={genericDamages}
          isDisplayOld
          isDisplayList={false}
          isViewOldEnabled
          isViewEnabled
          oldDamagesDisplay={{
            defaultStates: [],
            isStateTogglesEnabled: true,
          }}
          onView={(damage) => {
            if (damage.type === "current") {
              setDamageDataToView(damage.data);
            } else if (damage.type === "original") {
              setDamageToView(damage.data);
            }
          }}
          onCreated={noop}
          onUpdated={noop}
          onDeleted={noop}
          onManyChanged={noop}
        />
      )}

      {/* Vehicle damage data view modal */}
      {damageDataToView && (
        <VehicleDamageDataViewModal
          open={!!damageDataToView}
          onClose={() => {
            setDamageDataToView(null);
          }}
          viewProps={{
            entityData: damageDataToView,
          }}
        />
      )}

      {/* Vehicle damage view modal */}
      {damageToView && (
        <VehicleDamageViewModal
          open={!!damageToView}
          onClose={() => {
            setDamageToView(null);
          }}
          viewProps={{
            vehicleId: damageToView.vehicle?.id,
            vehicleDamageId: damageToView.id,
            vehicleDamage: damageToView,
            onDelete: null,
          }}
        />
      )}
    </Box>
  );
}
